import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../layouts/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar, Eye, Clock, CheckCircle, MapPin, Home, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import calendarIcon from '../../assets/img3d/calendar.png';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyVisitsPage = () => {
  const navigate = useNavigate();
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('upcoming');
  const [submittingDecision, setSubmittingDecision] = useState(null);

  useEffect(() => {
    fetchVisits();
  }, [filter]);

  const fetchVisits = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('upcoming_only', filter === 'upcoming' ? 'true' : 'false');

      const response = await axios.get(`${API}/client/my-visits?${params.toString()}`);
      // L'API retourne un tableau de visites enrichies
      const visitsData = response.data || [];
      setVisits(Array.isArray(visitsData) ? visitsData : []);
    } catch (error) {
      console.error('Error fetching visits:', error);
      setVisits([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateWithTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (duration) => {
    if (!duration) return '60 min';
    return `${duration} min`;
  };

  const getVisitStatusBadge = (visit) => {
    const isCompleted = visit.visit_completed || visit.visitCompleted;
    const visitDate = visit.visit_date || visit.visitDate;
    const now = new Date();
    const visitDateTime = visitDate ? new Date(visitDate) : null;

    if (isCompleted) {
      return (
        <Badge className="bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          Terminée
        </Badge>
      );
    }

    if (visitDateTime && visitDateTime < now) {
      return (
        <Badge className="bg-gray-100 text-gray-800">
          <Clock className="h-3 w-3 mr-1" />
          Passée
        </Badge>
      );
    }

    return (
      <Badge className="bg-yellow-100 text-yellow-800">
        <Calendar className="h-3 w-3 mr-1" />
        À venir
      </Badge>
    );
  };

  return (
    <Layout
      headerConfig={{
        icon: calendarIcon,
        iconColor: 'bg-white',
        title: 'Mes Visites',
        subtitle: 'Consultez vos visites planifiées',
        primaryAction: 'Rechercher un Bien',
        secondaryAction: undefined,
        tertiaryAction: undefined,
        onPrimaryAction: () => navigate('/properties'),
        onSecondaryAction: undefined,
        onTertiaryAction: undefined
      }}
    >
      {/* Filter */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">Filtrer les visites</label>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="upcoming">À venir</SelectItem>
                <SelectItem value="all">Toutes les visites</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Visits List */}
      {loading ? (
        <div className="text-center py-20">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-2 border-[#FF9F68]"></div>
        </div>
      ) : visits.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Calendar className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg text-gray-600 mb-2">
              {filter === 'upcoming'
                ? 'Aucune visite à venir'
                : 'Aucune visite trouvée'}
            </p>
            <p className="text-sm text-gray-500 mb-4">
              {filter === 'upcoming'
                ? 'Vos visites planifiées apparaîtront ici'
                : 'Vous n\'avez pas encore de visites planifiées'}
            </p>

          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {visits.map((visit) => {
            const property = visit.property || {};
            const visitDate = visit.visit_date || visit.visitDate;
            const visitDuration = visit.visit_duration || visit.visitDuration || 60;
            const propertyId = visit.property_id || visit.propertyId;
            const propertyTitle = property.title || property.name || 'Propriété';
            const propertyAddress = property.address || property.location || '';
            const propertyPrice = property.price || property.askingPrice;

            return (
              <Card key={visit.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-lg font-semibold text-gray-900">
                          Visite #{visit.id.slice(0, 8)}
                        </h3>
                        {getVisitStatusBadge(visit)}
                      </div>

                      {/* Property Information */}
                      {property && (
                        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-start gap-3">
                            <Home className="h-5 w-5 text-[#FF9F68] mt-0.5" />
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900 mb-1">
                                {propertyTitle}
                              </h4>
                              {propertyAddress && (
                                <div className="flex items-center gap-1 text-sm text-gray-600 mb-2">
                                  <MapPin className="h-4 w-4" />
                                  {propertyAddress}
                                </div>
                              )}
                              {propertyPrice && (
                                <p className="text-sm font-medium text-[#FF9F68]">
                                  {new Intl.NumberFormat('fr-FR', {
                                    style: 'currency',
                                    currency: 'EUR',
                                    maximumFractionDigits: 0
                                  }).format(propertyPrice)}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Visit Information */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 mb-4">
                        {visitDate && (
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-purple-600" />
                            <span className="font-medium text-gray-900">
                              {formatDateWithTime(visitDate)}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-orange-600" />
                          Durée : {formatDuration(visitDuration)}
                        </div>
                        {(visit.visit_notes || visit.visitNotes) && (
                          <div className="flex items-start gap-2">
                            <span className="text-gray-500">Notes :</span>
                            <span className="text-gray-700">
                              {visit.visit_notes || visit.visitNotes}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Request Link */}
                      <div className="text-sm text-gray-500">
                        <span>Demande de contact : </span>
                        <button
                          onClick={() => navigate(`/client/my-requests/${visit.id}`)}
                          className="text-[#FF9F68] hover:underline"
                        >
                          Voir la demande
                        </button>
                      </div>

                      {/* Decision Buttons - Show if visit completed but no final decision */}
                      {visit.visit_completed && !visit.visit_outcome && (
                        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm font-medium text-blue-900 mb-3">
                            Avez-vous pris une décision concernant ce bien ?
                          </p>
                          <div className="grid grid-cols-1 gap-2">
                            <Button
                              className="w-full bg-green-600 hover:bg-green-700 text-white"
                              disabled={submittingDecision !== null}
                              onClick={async () => {
                                setSubmittingDecision(visit.id);
                                try {
                                  await axios.post(
                                    `${API}/client/contact-requests/${visit.id}/visit-decision`,
                                    null,
                                    { params: { decision: 'WANT' } }
                                  );
                                  toast({
                                    title: 'Décision enregistrée',
                                    description: 'Votre décision a été transmise avec succès'
                                  });
                                  fetchVisits();
                                } catch (error) {
                                  console.error('Error submitting decision:', error);
                                  toast({
                                    title: 'Erreur',
                                    description: error.response?.data?.message || 'Impossible d\'enregistrer votre décision',
                                    variant: 'destructive'
                                  });
                                } finally {
                                  setSubmittingDecision(null);
                                }
                              }}
                            >
                              {submittingDecision === visit.id ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <CheckCircle2 className="h-4 w-4 mr-2" />
                              )}
                              Je veux le bien
                            </Button>
                            <Button
                              variant="outline"
                              className="w-full border-gray-300 text-gray-700 hover:bg-gray-50"
                              disabled={submittingDecision !== null}
                              onClick={async () => {
                                setSubmittingDecision(visit.id);
                                try {
                                  await axios.post(
                                    `${API}/client/contact-requests/${visit.id}/visit-decision`,
                                    null,
                                    { params: { decision: 'THINKING' } }
                                  );
                                  toast({
                                    title: 'Décision enregistrée',
                                    description: 'Votre décision a été transmise avec succès'
                                  });
                                  fetchVisits();
                                } catch (error) {
                                  console.error('Error submitting decision:', error);
                                  toast({
                                    title: 'Erreur',
                                    description: error.response?.data?.message || 'Impossible d\'enregistrer votre décision',
                                    variant: 'destructive'
                                  });
                                } finally {
                                  setSubmittingDecision(null);
                                }
                              }}
                            >
                              Je réfléchis encore
                            </Button>
                            <Button
                              variant="outline"
                              className="w-full border-red-300 text-red-600 hover:bg-red-50"
                              disabled={submittingDecision !== null}
                              onClick={async () => {
                                setSubmittingDecision(visit.id);
                                try {
                                  await axios.post(
                                    `${API}/client/contact-requests/${visit.id}/visit-decision`,
                                    null,
                                    { params: { decision: 'NOT_INTERESTED' } }
                                  );
                                  toast({
                                    title: 'Décision enregistrée',
                                    description: 'Votre décision a été transmise avec succès'
                                  });
                                  fetchVisits();
                                } catch (error) {
                                  console.error('Error submitting decision:', error);
                                  toast({
                                    title: 'Erreur',
                                    description: error.response?.data?.message || 'Impossible d\'enregistrer votre décision',
                                    variant: 'destructive'
                                  });
                                } finally {
                                  setSubmittingDecision(null);
                                }
                              }}
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              Je ne veux pas le bien
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Show decision if already made */}
                      {visit.visit_outcome && (
                        <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <p className="text-sm text-gray-600">
                            <strong>Votre décision :</strong>{' '}
                            {visit.visit_outcome === 'OFFER_MADE' && 'Je veux le bien'}
                            {visit.visit_outcome === 'INTERESTED' && 'Je réfléchis encore'}
                            {visit.visit_outcome === 'NOT_INTERESTED' && 'Je ne veux pas le bien'}
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-2 ml-4">
                      {propertyId && (
                        <Button
                          variant="outline"
                          onClick={() => navigate(`/properties/${propertyId}`)}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Voir le bien
                        </Button>
                      )}
                      <Button
                        onClick={() => navigate(`/client/my-requests/${visit.id}`)}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Détails
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </Layout>
  );
};

export default MyVisitsPage;

